import { useParams } from 'react-router';

import Grid from '@mui/material/Grid';
import { Box, Card, Typography, CardContent } from '@mui/material';

import { usePolicyTimelineByPolicyId } from 'src/api/hooks/usePolicyDetailsQueries';

import { useSupportDrawerContext } from 'src/components/support-drawer/context/use-support-drawer-context';

import { useHeaderWidth } from '../../shared/hooks/useHeaderWidth';
import SharedTimeline from '../../shared/components/shared-timeline';
import { usePolicyDataFromParams } from '../../shared/hooks/usePolicyData';
import UnitStatSummarySection from './policy-info/unit-stat-summary-section';
import PolicyGeneralInformation from './policy-info/policy-general-information';
import AssignedRiskApplicationEntry from '../../assigned-risk-application/components/assigned-risk-application-entry';

export function PolicyGeneral() {
  const drawerContext = useSupportDrawerContext();

  const { comboId, coverageId, policyId } = useParams<{
    comboId: string;
    coverageId: string;
    policyId: string;
  }>();

  const headerWidth = useHeaderWidth();
  const { viewPolicy } = usePolicyDataFromParams();

  const { data } = usePolicyTimelineByPolicyId(
    Number(comboId),
    Number(coverageId),
    Number(policyId)
  );

  const timelineEvents = data?.data?.timelineEvents || [];
  const assignedRiskApplication = viewPolicy?.assignedRiskApplicationNumber
    ? {
        ...viewPolicy.assignedRiskApplication,
        assignedRiskAppId: viewPolicy.assignedRiskApplicationNumber,
        policyId: viewPolicy.policyId,
        carrierName: viewPolicy.carrier?.carrierName,
      }
    : null;

  const renderContent = () => (
    <>
      {/* Policy Information */}
      <Grid container spacing={2} columns={12}>
        {/* Policy Information Block */}
        <Grid size={{ xs: 12, md: drawerContext.openDrawer ? 12 : 8 }}>
          <Card
            sx={[
              () => ({
                display: 'flex',
                zIndex: 'unset',
                overflow: 'unset',
                mt: 5,
                borderRadius: 2,
                boxShadow: 'var(--customShadows-card)',
                width: headerWidth,
              }),
            ]}
          >
            <PolicyGeneralInformation
              comboId={comboId}
              coverageId={coverageId}
              viewPolicy={viewPolicy}
            />
          </Card>
          {assignedRiskApplication && comboId && coverageId ? (
            <Box sx={{ width: headerWidth }}>
              <Card
                sx={{
                  mt: 3,
                  borderRadius: 2,
                  boxShadow: 'var(--customShadows-card)',
                }}
                data-testid="assigned-risk-application-card"
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6">Assigned risk application</Typography>
                  <AssignedRiskApplicationEntry application={assignedRiskApplication} comboId={comboId} coverageId={coverageId} />
                </CardContent>
              </Card>
            </Box>
          ) : null}
          <Box sx={{ mt: 3, width: headerWidth }}>
            <UnitStatSummarySection
              comboId={comboId}
              coverageId={coverageId}
              policyId={viewPolicy?.policyId}
            />
          </Box>
        </Grid>
        {/* Policy timeline */}
        <Grid size={{ xs: 12, md: drawerContext.openDrawer ? 12 : 4 }}>
          <Card
            sx={[
              () => ({
                zIndex: 'unset',
                overflow: 'unset',
                mt: drawerContext.openDrawer ? 0 : 5,
                borderRadius: 2,
                boxShadow: 'var(--customShadows-card)',
                width: 'fit-content', // Allow the width to adjust dynamically
                maxWidth: headerWidth, // Ensure it doesn't exceed the calculated header width
              }),
            ]}
          >
            <SharedTimeline title="Policy timeline" timelineEvents={timelineEvents} />
          </Card>
        </Grid>
      </Grid>
    </>
  );
  return <>{renderContent()}</>;
}
